/**
 * Rule-based data-domain classifier. Given a column's name + data type and the
 * dataset it belongs to, it finds the best-matching domain and a confidence
 * score. Column-name keyword matching, a numeric-type boost, and a dataset
 * "context" boost (a column named `type` in a *transactions* table leans to
 * Transaction Type, not Account Type) combine into the score.
 */

import type { DataDomain } from '@/features/domains/types';

export interface DomainMatch {
  domainId: string;
  confidence: number;
}

const NUMERIC_RE = /int|num|dec|float|double|real|money|bigint|smallint/i;

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function tokenize(normalized: string): string[] {
  return normalized.split('_').filter(Boolean);
}

const GROUP_HINTS: Record<string, string[]> = {
  identity: ['customer', 'client', 'party', 'contact'],
  account: ['account', 'acct'],
  transaction: ['transaction', 'txn', 'trans', 'payment'],
  geography: [],
  compliance: ['risk', 'compliance', 'kyc'],
};

function contextBoost(groupId: string, datasetHint: string): number {
  const hints = GROUP_HINTS[groupId] ?? [];
  return hints.some((h) => datasetHint.includes(h)) ? 6 : 0;
}

/** Best domain for a single column, or null if nothing clears the threshold. */
export function classifyColumn(
  columnName: string,
  dataType: string,
  datasetName: string,
  domains: readonly DataDomain[],
): DomainMatch | null {
  const col = normalize(columnName);
  if (col === '') return null;
  const colTokens = tokenize(col);
  const datasetHint = normalize(datasetName);
  const isNumeric = NUMERIC_RE.test(dataType);

  let best: DomainMatch | null = null;

  for (const domain of domains) {
    let score = 0;
    for (const keyword of domain.keywords) {
      const k = normalize(keyword);
      let s = 0;
      if (col === k) s = 98;
      else if (colTokens.includes(k)) s = 88;
      else if (col.startsWith(`${k}_`) || col.endsWith(`_${k}`)) s = 84;
      else if (col.includes(k)) s = 74;
      if (s > score) score = s;
    }
    if (score === 0) continue;

    if (domain.numeric && isNumeric) score += 3;
    score += contextBoost(domain.groupId, datasetHint);
    if (score > 99) score = 99;

    if (best === null || score > best.confidence) {
      best = { domainId: domain.id, confidence: score };
    }
  }

  if (best === null || best.confidence < 60) return null;
  return best;
}
